import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ISalesforceObjectMapping } from '../../components/adv2-config/adv2-config';
import { SalesforceMappingStoreService } from '../salesforce/salesforce-mapping-store.service';

interface INamespaceMeta {
  namespace: string;
  objectNames: IEntityLabelPair[];
}

export interface IEntityLabelPair {
  objectName: string;
  label: string;
  fields?: any[];
  iconUrl?: string;
  iconColor?: string;
}

interface ISalesforceObjectSummary {
  name?: string;
  apiName?: string;
  label?: string;
  iconUrl?: string;
  iconColor?: string;
}

interface ISalesforceObjectInfo {
  label: string;
  iconUrl?: string;
  iconColor?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EntityTypeService {
  private subject = undefined;
  private obs$ = undefined;
  private salesforceObjectLabelsByObjectNames: Record<string, Observable<Record<string, ISalesforceObjectInfo>>> = {};

  constructor(private http: HttpClient, private salesforceMappingStore: SalesforceMappingStoreService) {
    this.clear();
  }

  clear(): void {
    // i don't like this - pulling the rug out from under the subscribers
    this.subject?.unsubscribe();
    this.subject = new ReplaySubject<IEntityLabelPair[]>(1);
    this.obs$ = undefined;
  }

  // @TODO: move salesforce specifics
  getEntitiesForNamespace(namespace: Namespace): Observable<IEntityLabelPair[]> {
    if (namespace === Namespace.salesforce) {
      return this.salesforceMappingStore.getEnabledMappings().pipe(
        switchMap((mappings) => {
          const enabledMappings = (mappings ?? []).filter((mapping) => mapping?.enabled !== false);
          return this.getSalesforceObjectLabels(enabledMappings.map((mapping) => mapping.objectName)).pipe(
            map((labels) => this.mapSalesforceEntities(enabledMappings, labels))
          );
        })
      );
    }

    if (this.obs$) {
      return this.obs$;
    }

    this.obs$ = this.subject.asObservable();

    const url = `${environment.kpHost}/datasource/${namespace}/meta?labels=true`;
    this.http
      .get<INamespaceMeta>(url)
      .pipe(map((response) => response.objectNames))
      .subscribe({
        next: (entityLabelPairList) => this.subject.next(entityLabelPairList),
        error: (err) => this.subject.error(err),
      });

    return this.obs$;
  }

  getEntityFieldsForNamespace(namespace: Namespace, entityName: string, fields?: string): Observable<IEntityLabelPair> {
    let url = `${environment.kpHost}/datasource/${namespace}/meta/${entityName}`;
    if (fields) {
      url += `?fields=${fields}`;
    }
    return this.http.get<IEntityLabelPair>(url);
  }

  private getSalesforceObjectLabels(objectNames: string[]): Observable<Record<string, ISalesforceObjectInfo>> {
    const normalizedObjectNames = Array.from(
      new Set((objectNames ?? []).map((objectName) => objectName?.trim()).filter(Boolean))
    );
    if (!normalizedObjectNames.length) {
      return of({});
    }

    const cacheKey = normalizedObjectNames.slice().sort().join(',');
    if (!this.salesforceObjectLabelsByObjectNames[cacheKey]) {
      const params = [
        `resolveIcons=true`,
        `objectNames=${encodeURIComponent(normalizedObjectNames.join(','))}`,
      ].join('&');
      const url = `${environment.kpHost}/salesforce/meta/objects?${params}`;
      this.salesforceObjectLabelsByObjectNames[cacheKey] = this.http.get<ISalesforceObjectSummary[]>(url).pipe(
        map((objects) =>
          (objects ?? []).reduce((acc, object) => {
            const apiName = (object.apiName ?? object.name ?? '').trim();
            const label = object.label?.trim() || apiName;
            if (apiName && label) {
              acc[apiName] = {
                label,
                iconUrl: object.iconUrl?.trim() || undefined,
                iconColor: object.iconColor?.trim() || undefined,
              };
            }
            return acc;
          }, {} as Record<string, ISalesforceObjectInfo>)
        ),
        catchError(() => of({})),
        shareReplay(1)
      );
    }
    return this.salesforceObjectLabelsByObjectNames[cacheKey];
  }

  private mapSalesforceEntities(
    mappings: ISalesforceObjectMapping[],
    rawLabels: Record<string, ISalesforceObjectInfo>
  ): IEntityLabelPair[] {
    return mappings
      .map((mapping) => {
        const objectName = mapping.objectName;
        const rawObjectInfo = rawLabels[objectName];
        const label = (rawObjectInfo?.label || objectName || '').trim() || objectName;
        const iconUrl = rawObjectInfo?.iconUrl?.trim() || undefined;
        const iconColor = rawObjectInfo?.iconColor?.trim() || undefined;

        return {
          objectName,
          label,
          iconUrl,
          iconColor,
          fields: mapping.fields ?? [],
        };
      })
      .filter((pair) => !!pair.objectName)
      .sort((a, b) => a.label.localeCompare(b.label));
  }
}
